import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  FlexImageParameters,
  FlexImageV2RequestParams,
  FullSummaryItem,
} from '../../../api/generated'
import { useClient } from '../../../utils/useClient'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { getMarketLanguageState } from '../../../store/appSlice'
import {
  getMarketDenomination,
  getSessionInitDataState,
  getStartupData,
  getSummaryOrRfqOpenedFromPage,
  pushPageViewTrackingEvent,
} from '../../../store/sessionDataSlice'
import { closeAllModals } from '../../../store/modalSlice'
import {
  TdsButton,
  TdsIcon,
  TdsTable,
  TdsTableHeader,
  TdsHeaderCell,
  TdsTableBody,
  TdsTableBodyRow,
  TdsBodyCell,
} from '@scania/tegel-react'
import { SESSION_FAILURE } from '../../../api/errors'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { CustomScrollbarMixin } from '../../../css/CustomScrollbarMixin'
import { SvgCross, SvgPrint, SvgShare } from '../../../components/SvgImports'
import {
  getLatestConfigChangeResult,
  getTruckInfoState,
} from '../../../store/truckInfoSlice'
import {
  openGvPanel,
  setGvSidePanelContent,
} from '../../../store/sidePanelSlice'
import { GuidedViewPageType, SidePanelContent } from '../../../store/types'
import {
  buildPdfUrl,
  buildRelativeAllViewPage,
  buildRelativeGvPage,
  buildRelativeOverviewPage,
} from '../../../utils/UrlBuilders'
import {
  pushAdobeEvent,
  ScaniaAdobeDownloadType,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingDownloadPdfEvent,
  ScaniaAdobeTrackingPageName,
} from '../../../utils/adobeAnalytics'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import { useRfqEysButtonState } from '../RfqEysButtonState'
import { getUrlParametersToString } from '../../../utils/getUrlParametersToString'
import { useNavigate } from 'react-router-dom'
import { usePageUpdate } from '../../../utils/useBMPageUpdate'
import { FAMILY_ID_APPLICATION } from '../../../api/constants'
import { ActiveChoicesIcon } from '../ActiveChoicesIcon'
import { TestElementTypeId } from '../../../types/TestAttributeId'

const Overlay = styled.div`
  width: 100%;
  height: calc(100% - var(--header-height));
  max-width: 100vw;
  background-color: var(--tds-white);
  top: var(--header-height);
  position: fixed;
  padding-left: 40px;
  padding-right: 40px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  ${CustomScrollbarMixin}

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding-right: 16px;
    padding-left: 16px;
  }
`

const SubHeader = styled.div`
  height: 72px;
  display: flex;
  align-items: center;
  margin-top: 16px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const Header = styled.h1`
  display: flex;
  margin-top: 32px;
  margin-bottom: 40px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    font-size: 20px;
    margin-bottom: 0px;
    align-items: center;
    justify-content: space-between;
  }
`

const ContentWrapper = styled.div`
  display: flex;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: flex;
    flex-direction: column;
  }
`

const LeftSection = styled.div`
  width: 60%;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
  }
`

const RightSection = styled.div`
  width: 40%;
  height: 100%;
  margin-left: 16px;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const TableHeader = styled.h4`
  margin-bottom: 32px;
  margin-left: 16px;
  display: flex;
  align-items: center;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    font-size: 16px;
    margin-bottom: 16px;
    margin-left: 0px;
  }
`

const TableWrapper = styled.div`
  margin-bottom: 40px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin-bottom: 24px;
  }
`

const MobileTableWrapper = styled.div`
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: block;
  }
`

const DesktopTableWrapper = styled.div`
  display: block;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const MiniSummaryWrapper = styled.div`
  flex: 1;
`

const MobileTableTest = styled.div`
  font-weight: bold;
`

const TruckImageWrapper = styled.div`
  img {
    width: 100%;
  }
`

const TruckName = styled.div`
  font-size: 24px;
  margin-left: 32px;
  margin-bottom: 16px;
`

const TruckNameMobile = styled.div`
  padding-bottom: 16px;
  font-size: 20px;
  margin-left: 0px;
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: unset;
  }
`

const TruckImageText = styled.div`
  margin-left: 32px;
  margin-bottom: 16px;
`

const TruckIconsWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  margin-left: 32px;
  gap: 32px;
`

const MiniSummaryButtonWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: end;
`

const IconWrapper = styled.div`
  svg {
    height: 24px;
    width: 24px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

const IconText = styled.div`
  font-size: 11px;
`

const ButtonWrapper = styled.div`
  margin-left: 32px;
`

const MobileCloseWrapper = styled.div`
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: unset;
  }

  svg {
    height: 24px;
    width: 24px;
  }
`

export interface SummaryProps {
  handleFatalError: () => void
  handleSessionFailure: () => void
  handleRfqOrEysClick: () => void
}

export function SummaryPage({
  handleFatalError,
  handleSessionFailure,
  handleRfqOrEysClick,
}: SummaryProps): JSX.Element {
  const dispatch = useAppDispatch()
  const client = useClient()
  const navigate = useNavigate()
  const t2 = useTextsV2()
  const startupData = useAppSelector(getStartupData)
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const sessionId = sessionInitData?.sessionId
  const marketDenomination = useAppSelector(getMarketDenomination)
  const truckInfo = useAppSelector(getTruckInfoState)
  const summaryOrRfqOpenedFromPage = useAppSelector(
    getSummaryOrRfqOpenedFromPage,
  )
  const activeChoicesState = useAppSelector(
    getLatestConfigChangeResult,
  )?.activeChoices
  const { rfqOrEysButtonHidden, rfqOrEysButtonText, rfqOrEysButtonEnabled } =
    useRfqEysButtonState()
  const tableRef = useRef<HTMLDivElement | null>(null)

  const [imageJpgUrl, setImageJpgUrl] = useState<string | undefined>(undefined)
  const [summarySpecification, setSummarySpecification] = useState<
    FullSummaryItem | undefined | null
  >(undefined)
  const [
    summaryItemsWithoutSpecification,
    setSummaryItemsWithoutSpecification,
  ] = useState<FullSummaryItem[] | undefined | null>(undefined)

  const handlePdfDownloadButtonClick = useCallback(() => {
    if (!startupData) {
      throw new Error('Expected startupData to be defined.')
    }
    if (!marketLanguage) {
      throw new Error('Expected marketLanguage to be defined.')
    }
    if (!sessionInitData) {
      throw new Error('Expected sessionInitData to be defined.')
    }
    const pdfLink = buildPdfUrl(
      startupData.backendUrl,
      sessionInitData.sessionId,
      marketLanguage.language,
      marketDenomination,
    )
    const trackingEvent: ScaniaAdobeTrackingDownloadPdfEvent = {
      event: ScaniaAdobeEventId.Download,
      eventInfo: {
        downloadType: ScaniaAdobeDownloadType.Pdf,
        filePath: pdfLink,
      },
    }
    pushAdobeEvent(trackingEvent)
    window.open(pdfLink, '_blank', 'noreferrer')
  }, [marketDenomination, marketLanguage, sessionInitData, startupData])

  useEffect(() => {
    async function getSummary() {
      if (!marketLanguage || !client || !sessionId) {
        return
      }
      const response = await client.getSummary(
        sessionId,
        marketLanguage.language,
      )
      let summaryGeneral: FullSummaryItem | null = null
      let summaryWithoutGeneral: FullSummaryItem[] = []

      if (!response.scaniaGetSummary?.summaryItems) {
        throw new Error('Expected minisummary items.')
      }
      response.scaniaGetSummary?.summaryItems.forEach((item) => {
        const fullSummaryItem: FullSummaryItem = {
          header: item.header,
          families: item.families,
          isActiveChanges: item.isActiveChanges,
        }
        if (
          item.families.some((item) =>
            item.etelCode?.includes(FAMILY_ID_APPLICATION),
          )
        ) {
          summaryGeneral = fullSummaryItem
        } else {
          summaryWithoutGeneral.push(fullSummaryItem)
        }
      })

      setSummarySpecification(summaryGeneral)
      setSummaryItemsWithoutSpecification(summaryWithoutGeneral)
    }

    getSummary()
  }, [client, marketLanguage, sessionId])

  // Build url for image
  useEffect(() => {
    async function getImageUrl(imageType: string, bgColorArgb: string) {
      if (!client || !sessionId) {
        return
      }
      const flexParams: FlexImageParameters = {
        series: 'EXTERIOR',
        frame: 2,
        bgColor: bgColorArgb,
        autoCrop: ['top', 'bottom', 'left', 'right'],
        margin: [40, 40, 40, 40],
        imageType: imageType,
        size: [1280, 1024],
        useBoundingBoxes: false,
      }
      const requestParams: FlexImageV2RequestParams = {
        sessionId,
        flexParams,
        showBodyBuild: false,
      }
      const response = await client.flexImageV2(requestParams)
      if (response.error === SESSION_FAILURE) {
        return null
      }
      if (response.error) {
        console.error(response.error)
        return null
      }
      if (!response.success?.url) {
        console.error('Failed to find image URL in response.')
        return null
      }
      return response.success.url
    }
    getImageUrl('jpg', '#FFFFFFFF').then((result) => {
      setImageJpgUrl(result || undefined)
    })
  }, [client, sessionId])

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(openGvPanel())
    dispatch(setGvSidePanelContent(SidePanelContent.share))
  }

  const handleCloseClick = useCallback(() => {
    dispatch(closeAllModals())
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    const relativeUrl =
      summaryOrRfqOpenedFromPage === GuidedViewPageType.allView
        ? buildRelativeAllViewPage(marketLanguage)
        : summaryOrRfqOpenedFromPage === GuidedViewPageType.guidedView
        ? buildRelativeGvPage(marketLanguage)
        : buildRelativeOverviewPage(marketLanguage)
    const params = relativeUrl.params
    const newUrl = relativeUrl.path + getUrlParametersToString(params)
    navigate(newUrl)
  }, [dispatch, marketLanguage, navigate, summaryOrRfqOpenedFromPage])

  usePageUpdate(handleFatalError, handleSessionFailure)

  // Adobe user tracking.
  useEffect(() => {
    if (!marketLanguage) {
      return
    }
    dispatch(
      pushPageViewTrackingEvent({
        pageName: ScaniaAdobeTrackingPageName.GvSummary,
        marketLanguage,
      }),
    )
  }, [dispatch, marketLanguage])

  return (
    <Overlay>
      <SubHeader>
        <TdsButton
          variant="ghost"
          text={t2(TextId.ACTION_LABEL_BACK_CONFIGURATION)}
          onClick={handleCloseClick}
          data-test-element-type={TestElementTypeId.SubHeaderGoBackButton}
        >
          <TdsIcon slot="icon" size="20px" name="back" />
        </TdsButton>
      </SubHeader>
      <ContentWrapper>
        <LeftSection
          data-test-element-type={TestElementTypeId.SummaryLeftSection}
        >
          <Header>
            {t2(TextId.LABEL_ACTION_SUMMARY)}

            <MobileCloseWrapper onClick={handleCloseClick}>
              <SvgCross />
            </MobileCloseWrapper>
          </Header>
          <TruckNameMobile>{marketDenomination}</TruckNameMobile>

          {summarySpecification && (
            <DesktopTableWrapper>
              <TableHeader>{t2(TextId.HEADER_SPECIFICATION)}</TableHeader>
              <TableWrapper>
                <TdsTable
                  compactDesign={true}
                  responsive={true}
                  verticalDividers={true}
                  noMinWidth
                >
                  <TdsTableHeader>
                    <TdsHeaderCell
                      cellKey="item"
                      cellValue={t2(TextId.LABEL_ITEM)}
                      customWidth={
                        tableRef.current?.offsetWidth
                          ? (tableRef.current?.offsetWidth * 0.5).toString() +
                            'px'
                          : '50%'
                      }
                    />
                    <TdsHeaderCell
                      cellKey="selection"
                      cellValue={t2(TextId.LABEL_SELECTION)}
                      customWidth={
                        tableRef.current?.offsetWidth
                          ? (tableRef.current?.offsetWidth * 0.5).toString() +
                            'px'
                          : '50%'
                      }
                    />
                  </TdsTableHeader>
                  <TdsTableBody>
                    {summarySpecification.families.map((item) => {
                      return (
                        <TdsTableBodyRow key={'desktopSpec' + item.etelCode}>
                          <TdsBodyCell
                            cellKey="item"
                            cellValue={item.familyText || undefined}
                            style={{ wordBreak: 'break-word' }}
                          />
                          <TdsBodyCell
                            cellKey="selection"
                            cellValue={item.executionText || undefined}
                            style={{ wordBreak: 'break-word' }}
                          />
                        </TdsTableBodyRow>
                      )
                    })}
                  </TdsTableBody>
                </TdsTable>
              </TableWrapper>
            </DesktopTableWrapper>
          )}

          {summarySpecification && (
            <MobileTableWrapper>
              <TableHeader>{t2(TextId.HEADER_SPECIFICATION)}</TableHeader>
              <TableWrapper>
                <TdsTable
                  compactDesign={true}
                  responsive={true}
                  verticalDividers={true}
                  noMinWidth
                >
                  <TdsTableHeader>
                    <TdsHeaderCell
                      cellKey="item"
                      cellValue={t2(TextId.LABEL_ITEM)}
                    />
                  </TdsTableHeader>
                  <TdsTableBody>
                    {summarySpecification.families.map((item) => {
                      return (
                        <TdsTableBodyRow key={'mobileSpec' + item.etelCode}>
                          <TdsBodyCell
                            cellKey="item"
                            style={{ wordBreak: 'break-word' }}
                          >
                            <MobileTableTest>{item.familyText}</MobileTableTest>
                            {item.executionText}
                          </TdsBodyCell>
                        </TdsTableBodyRow>
                      )
                    })}
                  </TdsTableBody>
                </TdsTable>
              </TableWrapper>
            </MobileTableWrapper>
          )}

          {summaryItemsWithoutSpecification &&
            summaryItemsWithoutSpecification.map((group) => {
              return (
                <DesktopTableWrapper
                  key={'DesktopSpec' + group.header}
                  ref={tableRef}
                >
                  <TableHeader>
                    {group.header}
                    {group.isActiveChanges && (
                      <ActiveChoicesIcon
                        choiceCount={activeChoicesState?.length || 0}
                        isHighlighted={false}
                      />
                    )}
                  </TableHeader>
                  <TableWrapper>
                    <TdsTable
                      compactDesign={true}
                      responsive={true}
                      verticalDividers={true}
                      noMinWidth={true}
                    >
                      <TdsTableHeader>
                        <TdsHeaderCell
                          cellKey="item"
                          cellValue={t2(TextId.LABEL_ITEM)}
                          customWidth={
                            tableRef.current?.offsetWidth
                              ? (
                                  tableRef.current?.offsetWidth * 0.5
                                ).toString() + 'px'
                              : '50%'
                          }
                        />
                        <TdsHeaderCell
                          cellKey="selection"
                          cellValue={t2(TextId.LABEL_SELECTION)}
                          customWidth={
                            tableRef.current?.offsetWidth
                              ? (
                                  tableRef.current?.offsetWidth * 0.5
                                ).toString() + 'px'
                              : '50%'
                          }
                        />
                      </TdsTableHeader>
                      <TdsTableBody>
                        {group.families &&
                          group.families.map((item) => {
                            return (
                              <TdsTableBodyRow
                                key={'DesctopRow' + item.etelCode}
                              >
                                <TdsBodyCell
                                  cellKey="item"
                                  cellValue={item.familyText || undefined}
                                  style={{ wordBreak: 'break-word' }}
                                />
                                <TdsBodyCell
                                  cellKey="selection"
                                  cellValue={item.executionText || undefined}
                                  style={{ wordBreak: 'break-word' }}
                                />
                              </TdsTableBodyRow>
                            )
                          })}
                      </TdsTableBody>
                    </TdsTable>
                  </TableWrapper>
                </DesktopTableWrapper>
              )
            })}

          {summaryItemsWithoutSpecification &&
            summaryItemsWithoutSpecification.map((group) => {
              return (
                <MobileTableWrapper key={'mobileSummary' + group.header}>
                  <TableHeader>
                    {group.header}
                    {group.isActiveChanges && (
                      <ActiveChoicesIcon
                        choiceCount={activeChoicesState?.length || 0}
                        isHighlighted={false}
                      />
                    )}
                  </TableHeader>
                  <TableWrapper>
                    <TdsTable
                      compactDesign={true}
                      responsive={true}
                      verticalDividers={true}
                      noMinWidth
                    >
                      <TdsTableHeader>
                        <TdsHeaderCell
                          cellKey="item"
                          cellValue={t2(TextId.LABEL_ITEM)}
                        />
                      </TdsTableHeader>
                      <TdsTableBody>
                        {group.families &&
                          group.families.map((item) => {
                            return (
                              <TdsTableBodyRow
                                key={'MobileRow' + item.etelCode}
                              >
                                <TdsBodyCell
                                  cellKey="item"
                                  style={{ wordBreak: 'break-word' }}
                                >
                                  <MobileTableTest>
                                    {item.familyText}
                                  </MobileTableTest>
                                  {item.executionText}
                                </TdsBodyCell>
                              </TdsTableBodyRow>
                            )
                          })}
                      </TdsTableBody>
                    </TdsTable>
                  </TableWrapper>
                </MobileTableWrapper>
              )
            })}
        </LeftSection>

        <RightSection
          data-test-element-type={TestElementTypeId.SummaryRightSection}
        >
          <MiniSummaryWrapper>
            <TruckImageWrapper>
              <img src={imageJpgUrl} alt="Your truck"></img>
            </TruckImageWrapper>
            <TruckName>
              {truckInfo?.savedAsName ||
                truckInfo?.factoryModelName ||
                marketDenomination}
            </TruckName>
            <TruckImageText>{t2(TextId.DESCRIPTION_SUMMARY)}</TruckImageText>
            <TruckIconsWrapper>
              <IconWrapper
                onClick={handleShareClick}
                data-test-element-type={TestElementTypeId.SummaryShareButton}
              >
                <SvgShare />
                <IconText>{t2(TextId.LABEL_ACTION_SHARE)}</IconText>
              </IconWrapper>
              <IconWrapper
                onClick={handlePdfDownloadButtonClick}
                data-test-element-type={TestElementTypeId.SummaryPrintButton}
              >
                <SvgPrint />
                <IconText>{t2(TextId.LABEL_ACTION_PRINT)}</IconText>
              </IconWrapper>
            </TruckIconsWrapper>
            {!rfqOrEysButtonHidden && (
              <ButtonWrapper>
                <TdsButton
                  text={rfqOrEysButtonText}
                  fullbleed
                  size="md"
                  disabled={!rfqOrEysButtonEnabled}
                  onClick={handleRfqOrEysClick}
                  data-test-element-type={
                    TestElementTypeId.SummaryGetConsultationButton
                  }
                >
                  <TdsIcon slot="icon" size="20px" name="message" />
                </TdsButton>
              </ButtonWrapper>
            )}
          </MiniSummaryWrapper>
          <MiniSummaryButtonWrapper>
            <TdsButton
              variant="ghost"
              text={t2(TextId.ACTION_LABEL_BACK_CONFIGURATION)}
              onClick={handleCloseClick}
              data-test-element-type={TestElementTypeId.SummaryGoBackButton}
            >
              <TdsIcon slot="icon" size="20px" name="back" />
            </TdsButton>
          </MiniSummaryButtonWrapper>
        </RightSection>
      </ContentWrapper>
    </Overlay>
  )
}
